import { createSignal, createEffect, Show } from "solid-js";
import { useNavigate, useParams } from "@solidjs/router";
import { useAuth } from "../contexts/AuthContext";
import toast from "solid-toast";
import { getSession, updateSession } from "../api/sessions";
import { LANG_OPTIONS } from "../const/azureSpeechLangs";
import { useProfile } from "../contexts/ProfileContext";
import { getContactTypeOptions } from "../config/contactTypes";

export default function SessionSetup() {
  const params = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { profile } = useProfile();
  const contactTypeOptions = () => getContactTypeOptions(profile());

  const [contactType, setContactType] = createSignal("");
  const [respondent, setRespondent] = createSignal("");
  const [sourceLang, setSourceLang] = createSignal("en-US");
  const [targetLang, setTargetLang] = createSignal("");
  const [caseNumber, setCaseNumber] = createSignal("");
  const [isSubmitting, setIsSubmitting] = createSignal(false);
  const [sessionStatus, setSessionStatus] = createSignal<string>("draft");
  const [interpreterMode, setInterpreterMode] = createSignal(false);
  const [speaker1Lang, setSpeaker1Lang] = createSignal("en-US");
  const [speaker2Lang, setSpeaker2Lang] = createSignal("");
  const [validationMsg, setValidationMsg] = createSignal<string>("");
  const [captureMode, setCaptureMode] = createSignal<"mic" | "multi" | "file" | "internal_voicemail">("mic");
  const [selectedFile, setSelectedFile] = createSignal<File | null>(null);

  // Load existing session data if any
  createEffect(() => {
    if (params.id) {
      getSession(params.id)
        .then((session) => {
          if (session.status === "completed") {
            toast.error("Cannot modify a finalized session");
            navigate("/sessions");
            return;
          }

          setContactType(session.contactType || "");
          setRespondent(session.respondentName || "");
          setSourceLang(session.sourceLanguage || "en-US");
          setTargetLang(session.targetLanguage || "");
          setCaseNumber(session.case_number || "");
          setSessionStatus(session.status);
          const meta: any = (session as any).metadata || {};
          const isInterp = Boolean(meta.interpreterMode);
          setInterpreterMode(isInterp);
          setSpeaker1Lang(
            meta.speaker1Lang || session.sourceLanguage || "en-US",
          );
          setSpeaker2Lang(meta.speaker2Lang || session.targetLanguage || "");
          const incomingMode = meta.captureMode || "mic";
          setCaptureMode(
            incomingMode === "mic" || incomingMode === "multi" || incomingMode === "file"
              ? incomingMode
              : "mic",
          );
        })
        .catch((error) => {
          console.error("Failed to load session:", error);
          toast.error("Failed to load session");
          navigate("/sessions");
        });
    }
  });

  // Validation
  const isValid = () => {
    if (!contactType() || !respondent()) return false;
    if (captureMode() === "file" && !selectedFile()) return false;
    if (interpreterMode()) {
      const valid = Boolean(speaker1Lang()) && Boolean(speaker2Lang());
      const same = speaker1Lang() === speaker2Lang();
      setValidationMsg(
        same
          ? "Both speakers selected the same language—use single-language mode instead"
          : "",
      );
      return valid && !same;
    }
    setValidationMsg("");
    return Boolean(sourceLang());
  };

  // Save and proceed
  const handleStartInterview = async (e: Event) => {
    e.preventDefault();
    if (!isValid()) {
      toast.error("Please fill all required fields");
      return;
    }

    setIsSubmitting(true);
    try {
      // Build payload depending on mode
      const updates: any = {
        contact_type: contactType(),
        respondent_name: respondent(),
        case_number: caseNumber(),
        interviewer_name: auth.user()?.name || "Unknown",
      };

      if (interpreterMode()) {
        updates.source_language = speaker1Lang();
        updates.target_language = speaker2Lang();
        updates.metadata = {
          interpreterMode: true,
          speaker1Lang: speaker1Lang(),
          speaker2Lang: speaker2Lang(),
          captureMode: captureMode(),
        };
      } else {
        updates.source_language = sourceLang();
        updates.target_language = targetLang();
        updates.metadata = {
          interpreterMode: false,
          speaker1Lang: null,
          speaker2Lang: null,
          captureMode: captureMode(),
        };
      }

      // Save session setup
      await updateSession(params.id, updates);

      // If in file mode, store the file for the session page
      if (captureMode() === "file" && selectedFile()) {
        (window as any)._pendingUploadFile = selectedFile();
      }

      // Navigate to interview
      navigate(`/sessions/${params.id}`);
    } catch (error) {
      console.error("Failed to save session:", error);
      toast.error("Failed to save session setup");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div class="min-h-screen bg-gray-50">
      <div class="w-full max-w-none mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div class="bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div class="bg-blue-600 px-6 py-2 relative">
            <div class="absolute top-4 left-4">
              <button
                onClick={() => navigate("/sessions")}
                class="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 text-white text-sm font-bold flex items-center justify-center border border-white/30"
                title="Back to Sessions"
                aria-label="Home"
              >
                ⌂
              </button>
            </div>
            <h1 class="text-2xl font-bold text-white text-center">
              New Session Setup
            </h1>
            <p class="text-blue-100 mt-1 text-center">
              Configure your session before starting
            </p>
          </div>

          <form onSubmit={handleStartInterview} class="p-6 space-y-6">
            {/* Contact Type */}
            <div>
              <label class="block text-sm font-medium text-gray-700">
                Contact Type <span class="text-red-500">*</span>
              </label>
              <select
                value={contactType()}
                onChange={(e) => setContactType(e.currentTarget.value)}
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              >
                <option value="">Select Type</option>
                {contactTypeOptions().map((option) => (
                  <option value={option.value}>
                    {option.icon} {option.label}
                  </option>
                ))}
                <Show
                  when={
                    contactType() &&
                    !contactTypeOptions().some(
                      (option) => option.value === contactType(),
                    )
                  }
                >
                  <option value={contactType()}>{contactType()}</option>
                </Show>
              </select>
            </div>

            {/* Respondent Name */}
            <div>
              <label class="block text-sm font-medium text-gray-700">
                Respondent Name <span class="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={respondent()}
                onInput={(e) => setRespondent(e.currentTarget.value)}
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                required
              />
            </div>

            {/* Capture Mode */}
            <div>
              <label class="block text-sm font-medium text-gray-700">
                Capture Mode <span class="text-red-500">*</span>
              </label>
              <div class="mt-1 flex flex-wrap items-center gap-4">
                <label class="inline-flex items-center">
                  <input
                    type="radio"
                    name="captureMode"
                    value="mic"
                    checked={captureMode() === "mic"}
                    onChange={(e) =>
                      setCaptureMode(
                        e.currentTarget.value as "mic" | "multi",
                      )
                    }
                    class="form-radio"
                  />
                  <span class="ml-2">Device's microphone</span>
                </label>
                <label class="inline-flex items-center">
                  <input
                    type="radio"
                    name="captureMode"
                    value="multi"
                    checked={captureMode() === "multi"}
                    onChange={(e) =>
                      setCaptureMode(
                        e.currentTarget.value as "mic" | "multi" | "file",
                      )
                    }
                    class="form-radio"
                  />
                  <span class="ml-2">Multi-device (QR/code)</span>
                </label>
                <label class="inline-flex items-center">
                  <input
                    type="radio"
                    name="captureMode"
                    value="file"
                    checked={captureMode() === "file"}
                    onChange={(e) =>
                      setCaptureMode(
                        e.currentTarget.value as "mic" | "multi" | "file",
                      )
                    }
                    class="form-radio"
                  />
                  <span class="ml-2">Audio File</span>
                </label>

                <label class="inline-flex items-center">
                  <input
                    disabled={true}
                    type="radio"
                    name="captureMode"
                    value="internal_voicemail"
                    checked={captureMode() === "internal_voicemail"}
                    onChange={(e) =>
                      setCaptureMode(
                        e.currentTarget.value as "mic" | "multi" | "file" | "internal_voicemail",
                      )
                    }
                    class="form-radio"
                  />
                  <span class="ml-2">Internal Voicemail</span>
                </label>
              </div>
              
              <Show when={captureMode() === "file"}>
                <div class="mt-3 p-4 border border-dashed border-gray-300 rounded-md bg-gray-50">
                  <label class="block text-sm font-medium text-gray-700 mb-2">
                    Upload Audio File <span class="text-red-500">*</span>
                  </label>
                  <input
                    type="file"
                    accept=".wav,audio/wav"
                    onChange={(e) => {
                      const file = e.currentTarget.files?.[0];
                      if (file) {
                        if (file.name.toLowerCase().endsWith('.wav')) {
                          setSelectedFile(file);
                        } else {
                          toast.error("Please select a valid .wav file");
                          e.currentTarget.value = "";
                        }
                      }
                    }}
                    class="block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-semibold
                      file:bg-blue-50 file:text-blue-700
                      hover:file:bg-blue-100"
                  />
                  <p class="mt-1 text-xs text-gray-500">
                    The file will be processed as a stream to generate suggestions.
                  </p>
                </div>
              </Show>
            </div>

            {/* Interpreter Mode Toggle */}
            <div class="flex items-center justify-between border rounded-md p-3">
              <div>
                <label class="block text-sm font-medium text-gray-800">
                  Interpreter Mode
                </label>
                <p class="text-xs text-gray-500">
                  Enable dual-language routing (Speaker 1 ↔ Speaker 2)
                </p>
              </div>
              <label class="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  class="sr-only peer"
                  checked={interpreterMode()}
                  onChange={(e) => {
                    const next = e.currentTarget.checked;
                    setInterpreterMode(next);
                    if (next) {
                      setSpeaker1Lang(sourceLang() || "en-US");
                      setSpeaker2Lang(targetLang() || "");
                    }
                  }}
                />
                <div class="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:h-5 after:w-5 after:bg-white after:rounded-full after:transition-all relative peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* Language Selection - Single Language Mode */}
            <Show when={!interpreterMode()}>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700">
                    Source Language <span class="text-red-500">*</span>
                  </label>
                  <select
                    value={sourceLang()}
                    onChange={(e) => setSourceLang(e.currentTarget.value)}
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  >
                    {Object.entries(LANG_OPTIONS).map(([code, name]) => (
                      <option value={code}>{name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700">
                    Target Language
                  </label>
                  <select
                    value={targetLang()}
                    onChange={(e) => setTargetLang(e.currentTarget.value)}
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  >
                    <option value="">None</option>
                    {Object.entries(LANG_OPTIONS)
                      .filter(([code]) => code !== sourceLang())
                      .map(([code, name]) => (
                        <option value={code}>{name}</option>
                      ))}
                  </select>
                </div>
              </div>
            </Show>

            {/* Language Selection - Interpreter Mode */}
            <Show when={interpreterMode()}>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label class="block text-sm font-medium text-gray-700">
                    Speaker 1 Language (caseworker)
                    <span class="text-red-500"> *</span>
                  </label>
                  <select
                    value={speaker1Lang()}
                    onChange={(e) => setSpeaker1Lang(e.currentTarget.value)}
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  >
                    {Object.entries(LANG_OPTIONS).map(([code, name]) => (
                      <option value={code}>{name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700">
                    Speaker 2 Language (respondent)
                    <span class="text-red-500"> *</span>
                  </label>
                  <select
                    value={speaker2Lang()}
                    onChange={(e) => setSpeaker2Lang(e.currentTarget.value)}
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    required
                  >
                    {Object.entries(LANG_OPTIONS).map(([code, name]) => (
                      <option value={code}>{name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <Show when={validationMsg()}>
                <p class="text-sm text-red-600">{validationMsg()}</p>
              </Show>
            </Show>

            {/* Case Number */}
            <div>
              <label class="block text-sm font-medium text-gray-700">
                Case Number
              </label>
              <input
                type="text"
                value={caseNumber()}
                onInput={(e) => setCaseNumber(e.currentTarget.value)}
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter case number if applicable"
              />
            </div>

            {/* Buttons */}
            <div class="flex flex-col sm:flex-row justify-between gap-4 pt-4 border-t">
              <button
                type="button"
                onClick={() => navigate("/sessions")}
                class="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 order-2 sm:order-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!isValid() || isSubmitting()}
                class={`px-6 py-2 text-sm font-medium rounded-md text-white order-1 sm:order-2
                  ${
                    isValid() && !isSubmitting()
                      ? "bg-blue-600 hover:bg-blue-700"
                      : "bg-gray-400 cursor-not-allowed"
                  }`}
              >
                {isSubmitting() ? "Saving..." : "Start →"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
